/**
 * Mock service requests — open + quoted + booked + completed mix, with the
 * dev customer owning roughly half so the customer dashboard shows real data.
 */

import type { Tables, Json } from '@/lib/db.types'
import { categoryId, customerUserId, requestEntityId, DEV_USER_IDS } from './ids'

type ServiceRequest = Tables<'service_requests'>

const NOW = Date.parse('2026-05-01T08:00:00.000Z')
const T = (days: number) => new Date(NOW + days * 86400_000).toISOString()

interface RequestSeed {
  customer_id: string
  category_slug: string
  title: string
  description: string
  city: string
  postal_code: string
  budget_min: number | null
  budget_max: number | null
  status: ServiceRequest['status']
  daysAgo: number
  expiresInDays: number
  preferred_date: string | null
  flexible_dates: boolean
  details: Record<string, string | number | boolean>
}

const DEV_C = DEV_USER_IDS.customer

const SEEDS: RequestSeed[] = [
  {
    customer_id: DEV_C, category_slug: 'hemstadning',
    title: 'Veckostädning av 3:a i Vasastan',
    description: 'Behöver hjälp med veckostädning av en 3:a på 78 kvm. Två hundar i hemmet. Gärna eftermiddagar.',
    city: 'Stockholm', postal_code: '11321',
    budget_min: 600, budget_max: 1100, status: 'open',
    daysAgo: 2, expiresInDays: 14, preferred_date: null, flexible_dates: true,
    details: { sqm: 78, rooms: 3, frequency: 'veckovis' },
  },
  {
    customer_id: DEV_C, category_slug: 'bohagsflytt',
    title: 'Bohagsflytt från Vasastan till Sundbyberg',
    description: 'Tre rum och kök, totalt ca 85 kvm. Hiss i båda husen. Vill ha allt klart på en dag.',
    city: 'Stockholm', postal_code: '11321',
    budget_min: 7000, budget_max: 12000, status: 'quoted',
    daysAgo: 5, expiresInDays: 9, preferred_date: T(14), flexible_dates: false,
    details: { from_sqm: 85, from_floor: 3, to_floor: 2, elevator: true, distance_km: 9 },
  },
  {
    customer_id: DEV_C, category_slug: 'elektriker',
    title: 'Installation av takarmaturer i nya köket',
    description: 'Fyra takarmaturer i nyrenoverat kök behöver kopplas in. Eldragning finns redan på plats.',
    city: 'Stockholm', postal_code: '11321',
    budget_min: 1500, budget_max: 3500, status: 'booked',
    daysAgo: 12, expiresInDays: -1, preferred_date: T(4), flexible_dates: false,
    details: { description: '4 takarmaturer kök', type: 'installation' },
  },
  {
    customer_id: DEV_C, category_slug: 'mobelmontering',
    title: 'Montering av IKEA garderober (PAX)',
    description: 'Två PAX-garderober (250 cm) ska monteras i sovrummet. Allt material levererat.',
    city: 'Stockholm', postal_code: '11321',
    budget_min: 1500, budget_max: 2500, status: 'completed',
    daysAgo: 28, expiresInDays: -14, preferred_date: T(-14), flexible_dates: false,
    details: { items: 2, source: 'IKEA' },
  },
  {
    customer_id: DEV_C, category_slug: 'malare',
    title: 'Måla om vardagsrum och hall',
    description: 'Vardagsrum (28 kvm) och hall (8 kvm) behöver ommålning. Stark färg ska täckas över.',
    city: 'Stockholm', postal_code: '11321',
    budget_min: 6000, budget_max: 12000, status: 'completed',
    daysAgo: 60, expiresInDays: -45, preferred_date: T(-50), flexible_dates: true,
    details: { sqm: 36, rooms: 2, surface: 'väggar' },
  },
  // Other customers (these power provider inbox / search activity)
  {
    customer_id: customerUserId(1), category_slug: 'vvs',
    title: 'Läckande kran i köket',
    description: 'Kranen droppar konstant och kan inte stängas av. Skulle behöva komma snabbt.',
    city: 'Stockholm', postal_code: '11635',
    budget_min: 800, budget_max: 2500, status: 'open',
    daysAgo: 1, expiresInDays: 14, preferred_date: null, flexible_dates: true,
    details: { description: 'Köksblandare läcker', type: 'kran' },
  },
  {
    customer_id: customerUserId(2), category_slug: 'flyttstadning',
    title: 'Flyttstädning 67 kvm Södermalm',
    description: 'Tvåa på Södermalm. Behöver flyttstädning innan överlämning till mäklare.',
    city: 'Stockholm', postal_code: '11823',
    budget_min: 2500, budget_max: 4500, status: 'open',
    daysAgo: 3, expiresInDays: 7, preferred_date: T(10), flexible_dates: false,
    details: { sqm: 67, oven: true, windows: true },
  },
  {
    customer_id: customerUserId(3), category_slug: 'tradfallning',
    title: 'Fälla två stora björkar i trädgården',
    description: 'Två björkar (~15 m) som måste fällas inför husbyggnation. Stubbfräsning önskas.',
    city: 'Solna', postal_code: '17070',
    budget_min: 5000, budget_max: 10000, status: 'quoted',
    daysAgo: 6, expiresInDays: 8, preferred_date: T(21), flexible_dates: true,
    details: { count: 2, height: 15, stump: true },
  },
  {
    customer_id: customerUserId(4), category_slug: 'snickare',
    title: 'Bygga väggfast TV-bänk',
    description: 'Önskar en specialbyggd väggfast TV-bänk i ek, ca 220 cm bred.',
    city: 'Stockholm', postal_code: '11248',
    budget_min: 8000, budget_max: 15000, status: 'open',
    daysAgo: 4, expiresInDays: 16, preferred_date: null, flexible_dates: true,
    details: { description: 'Väggfast TV-bänk ek 220 cm', sqm: 2 },
  },
  {
    customer_id: customerUserId(5), category_slug: 'kakelsattare',
    title: 'Kakling av badrum, 6 kvm',
    description: 'Hela badrummet (6 kvm) ska kaklas om. Tätskikt finns redan på plats.',
    city: 'Stockholm', postal_code: '11827',
    budget_min: 18000, budget_max: 30000, status: 'booked',
    daysAgo: 14, expiresInDays: -5, preferred_date: T(7), flexible_dates: false,
    details: { sqm: 6, area: 'badrum' },
  },
  {
    customer_id: customerUserId(6), category_slug: 'datohjalp',
    title: 'PC startar inte efter Windows-uppdatering',
    description: 'Stationär dator visar blå skärm direkt efter senaste Windows-uppdateringen.',
    city: 'Stockholm', postal_code: '11353',
    budget_min: 500, budget_max: 1500, status: 'completed',
    daysAgo: 19, expiresInDays: -5, preferred_date: T(-12), flexible_dates: true,
    details: { issue: 'BSOD efter uppdatering', device: 'dator' },
  },
  {
    customer_id: customerUserId(7), category_slug: 'grasklippning',
    title: 'Säsongsavtal gräsklippning villa',
    description: 'Villa med 600 kvm gräsmatta. Önskar varannan vecka under maj–september.',
    city: 'Solna', postal_code: '17141',
    budget_min: 4000, budget_max: 8000, status: 'quoted',
    daysAgo: 9, expiresInDays: 5, preferred_date: T(8), flexible_dates: true,
    details: { sqm: 600, frequency: 'varannan vecka' },
  },
  {
    customer_id: customerUserId(8), category_slug: 'hundvakt',
    title: 'Hundvakt över sommarsemestern',
    description: '12 nätter i juli. Snäll labrador, 30 kg. Van vid andra hundar.',
    city: 'Stockholm', postal_code: '11540',
    budget_min: 3000, budget_max: 6500, status: 'open',
    daysAgo: 7, expiresInDays: 21, preferred_date: T(60), flexible_dates: true,
    details: { days: 12, size: 'stor' },
  },
  {
    customer_id: customerUserId(1), category_slug: 'matteundervisning',
    title: 'Privatlärare i matematik åk 9',
    description: 'Min son ska ta nationella prov i matte. Behöver intensiv hjälp 1–2 ggr/vecka.',
    city: 'Uppsala', postal_code: '75234',
    budget_min: 1800, budget_max: 4000, status: 'completed',
    daysAgo: 40, expiresInDays: -20, preferred_date: T(-35), flexible_dates: true,
    details: { level: 'grundskola', sessions: 8 },
  },
  {
    customer_id: customerUserId(2), category_slug: 'pianoflytt',
    title: 'Flytta upright-piano från Östermalm till Bromma',
    description: 'Piano (180 kg) ska flyttas en trappa ner och en upp. Hiss finns endast på avsändarsidan.',
    city: 'Stockholm', postal_code: '11428',
    budget_min: 3500, budget_max: 6500, status: 'open',
    daysAgo: 0, expiresInDays: 21, preferred_date: T(28), flexible_dates: true,
    details: { piano_type: 'upright', floors: 2 },
  },
]

export const MOCK_REQUESTS: ServiceRequest[] = SEEDS.map((s, i) => {
  const n = i + 1
  const createdAt = new Date(NOW - s.daysAgo * 86400_000).toISOString()
  return {
    id: requestEntityId(n),
    customer_id: s.customer_id,
    category_id: categoryId(s.category_slug),
    title: s.title,
    description: s.description,
    details: s.details as Json,
    location: null,
    address: null,
    city: s.city,
    postal_code: s.postal_code,
    preferred_date: s.preferred_date,
    preferred_time: null,
    flexible_dates: s.flexible_dates,
    budget_min: s.budget_min,
    budget_max: s.budget_max,
    status: s.status,
    expires_at: new Date(NOW + s.expiresInDays * 86400_000).toISOString(),
    photos: [],
    created_at: createdAt,
    updated_at: createdAt,
  } satisfies ServiceRequest
})

export function findRequestById(id: string): ServiceRequest | undefined {
  return MOCK_REQUESTS.find((r) => r.id === id)
}
